import React from "react";
import { Delta, PaeiBars, fmtMinutes, fmtMoney, fmtLevel, isNum } from "./paeiPrimitives";

// Three KPI cards, never two. The redesign moves cycle time; the restaffing moves
// cost and fit. Folding them into one "optimised" column would credit the
// structural change with savings that actually came from moving a director off an
// intake step.
//
// Deltas are always against columns[0], shown on cards 2 and 3 only.

const ROWS = [
  { key: "cycle_time_minutes", label: "Cycle time", fmt: fmtMinutes, invert: false },
  { key: "labor_cost_per_case", label: "Labour cost / case (USD)", fmt: fmtMoney, invert: false },
  // The one metric where higher is better — invert it or the arrows lie.
  { key: "paei_fit", label: "PAEI fit", fmt: (v) => (isNum(v) ? Number(v).toFixed(1) : "—"), invert: true },
  { key: "under_qualified_slots", label: "Under-qualified slots", invert: false, slots: true },
  { key: "over_qualification_cost", label: "Paid-for surplus (USD)", fmt: fmtMoney, invert: false },
];

function KpiCard({ column, baseline, isLast }) {
  const showDeltas = baseline && column.key !== baseline.key;

  return (
    <div className={`pa-kpi-card${isLast ? " terminal" : ""}`}>
      <div className="pa-kpi-head">
        <div className="pa-kpi-label">{column.label}</div>
        <div className="pa-kpi-blurb">{column.blurb}</div>
      </div>

      <div className="pa-kpi-rows">
        {ROWS.map((row) => {
          const value = column[row.key];
          const display =
            row.slots ?
              `${column.under_qualified_slots ?? "—"} / ${column.slot_count ?? "—"}`
            : row.fmt(value);
          return (
            <div className="pa-kpi-row" key={row.key}>
              <span className="pa-kpi-row-label">{row.label}</span>
              <span className="pa-kpi-row-value">
                {display}
                {showDeltas && (
                  <Delta value={value} baseline={baseline[row.key]} invert={row.invert} />
                )}
              </span>
            </div>
          );
        })}
      </div>

      <div className="pa-kpi-profile">
        <div className="pa-kpi-profile-title">Demand profile</div>
        <PaeiBars values={column.demand_profile} />
      </div>

      {column.coverage && (
        <div className="pa-kpi-coverage">
          <div className="pa-kpi-profile-title">Best cover held</div>
          <ul>
            {["p", "a", "e", "i"].map((k) => {
              const c = column.coverage[k];
              if (!c) return null;
              return (
                <li key={k}>
                  <span className={`pa-bar-label role-${k}`}>{k.toUpperCase()}</span>
                  <span className="pa-kpi-coverage-val">{fmtLevel(c.best)}</span>
                  <span className="pa-kpi-coverage-who">{c.held_by || "nobody staffed"}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function PaeiOverviewTab({ result }) {
  const columns = result?.columns || [];
  const baseline = columns[0] || null;
  const limitations = result?.limitations || [];

  return (
    <div className="pa-panel">
      {columns.length === 2 && result?.redesign?.reason && (
        <div className="pa-banner warn">
          <strong>The redesign could not run.</strong> {result.redesign.reason} Two columns are shown
          instead of three.
        </div>
      )}

      <div className="pa-kpi-grid">
        {columns.map((c, i) => (
          <KpiCard
            key={c.key || i}
            column={c}
            baseline={baseline}
            isLast={i === columns.length - 1 && columns.length > 1}
          />
        ))}
      </div>

      <p className="pa-note">
        Cycle time is identical for AS-IS and RESTAFFED by design — restaffing changes who holds a
        slot, not the structure of the process, so it cannot move. That is not "no improvement".
      </p>

      {limitations.length > 0 && (
        <section className="pa-card">
          <h3 className="pa-card-title">What these numbers cannot tell you</h3>
          <p className="pa-card-hint">
            Limits of the model itself, not caveats about your data. Read them before quoting any
            figure above.
          </p>
          <ul className="pa-limitations">
            {limitations.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default PaeiOverviewTab;
